import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MailPlus, Plus, ImagePlus, RotateCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RoomsManager } from "@/components/RoomsManager";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Page, PageHeader } from "@/components/common";
import { CalendarSettingsForms } from "@/components/CalendarSettingsForms";
import { StarterQuestionsSettings } from "@/components/StarterQuestionsSettings";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, ROLE_LABELS } from "@/lib/api";

function ExtraAccess({ role, perms, value, onChange }) {
  if (!perms || !perms.editable_roles.includes(role))
    return (
      <p
        className="text-xs text-muted-foreground"
        data-testid="extra-access-admin-note"
      >
        Admins always have full access.
      </p>
    );
  const base = perms.roles[role] || [];
  const toggle = (k) =>
    onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <div
      className="rounded-lg border divide-y max-h-56 overflow-y-auto"
      data-testid="extra-access-list"
    >
      {[...perms.catalog]
        .sort((a, b) => base.includes(a.key) - base.includes(b.key))
        .map((c) => (
          <label
            key={c.key}
            className="flex items-center gap-3 px-3 py-2 text-sm"
          >
            <span className="flex-1">
              <span className="text-[11px] text-muted-foreground block">
                {c.group}
              </span>
              {c.label}
            </span>
            {base.includes(c.key) ? (
              <span className="text-[11px] text-muted-foreground">
                Included with role
              </span>
            ) : (
              <Switch
                checked={value.includes(c.key)}
                onCheckedChange={() => toggle(c.key)}
                data-testid={`extra-perm-${c.key.replace(":", "-")}`}
              />
            )}
          </label>
        ))}
    </div>
  );
}

function UserDialog({ u, onClose, onSaved, perms }) {
  const [f, setF] = useState({
    email: "",
    name: "",
    role: "teacher",
    title: "",
    password: "",
    active: true,
    ...u,
    extra_perms: u.extra_perms || [],
  });
  const save = async () => {
    try {
      u.id
        ? await api.put(`/admin/users/${u.id}`, f)
        : await api.post("/admin/users", f);
      toast.success("User saved");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${u.name}'s account permanently?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-md max-h-[90vh] overflow-y-auto"
        data-testid="user-dialog"
      >
        <DialogHeader>
          <DialogTitle>{u.id ? "Edit user" : "Add user"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Email</Label>
            <Input
              disabled={!!u.id}
              value={f.email}
              onChange={(e) => setF({ ...f, email: e.target.value })}
              data-testid="user-email-input"
            />
          </div>
          <div className="space-y-1">
            <Label>Name</Label>
            <Input
              value={f.name}
              onChange={(e) => setF({ ...f, name: e.target.value })}
              data-testid="user-name-input"
            />
          </div>
          <div className="space-y-1">
            <Label>Job title</Label>
            <Input
              value={f.title}
              onChange={(e) => setF({ ...f, title: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label>Role</Label>
            <Select
              value={f.role}
              onValueChange={(v) => setF({ ...f, role: v })}
            >
              <SelectTrigger data-testid="user-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ROLE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Extra access for this person</Label>
            <ExtraAccess
              role={f.role}
              perms={perms}
              value={f.extra_perms}
              onChange={(v) => setF({ ...f, extra_perms: v })}
            />
          </div>
          <div className="space-y-1">
            <Label>
              {u.id
                ? "New password (optional)"
                : "Password (leave blank for Google sign-in only)"}
            </Label>
            <Input
              type="password"
              value={f.password}
              onChange={(e) => setF({ ...f, password: e.target.value })}
              data-testid="user-password-input"
            />
          </div>
          {u.id && (
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={f.active}
                onCheckedChange={(v) => setF({ ...f, active: v })}
              />
              Active
            </label>
          )}
        </div>
        <DialogFooter>
          {u.id && <Button variant="destructive" onClick={remove} data-testid="user-delete-button">Delete user</Button>}
          <Button onClick={save} data-testid="user-save-button">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InviteDialog({ onClose, onSaved, perms }) {
  const [f, setF] = useState({
    email: "",
    name: "",
    role: "teacher",
    title: "",
    extra_perms: [],
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/auth/invitations", f);
      toast.success("Account invitation sent");
      onSaved();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-h-[90vh] max-w-md overflow-y-auto"
        data-testid="invite-user-dialog"
      >
        <DialogHeader>
          <DialogTitle>Invite a user</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Email</Label>
            <Input
              type="email"
              value={f.email}
              onChange={(event) => setF({ ...f, email: event.target.value })}
              data-testid="invite-email-input"
            />
          </div>
          <div className="space-y-1">
            <Label>Name</Label>
            <Input
              value={f.name}
              onChange={(event) => setF({ ...f, name: event.target.value })}
              data-testid="invite-name-input"
            />
          </div>
          <div className="space-y-1">
            <Label>Job title</Label>
            <Input
              value={f.title}
              onChange={(event) => setF({ ...f, title: event.target.value })}
              data-testid="invite-title-input"
            />
          </div>
          <div className="space-y-1">
            <Label>Role</Label>
            <Select
              value={f.role}
              onValueChange={(role) => setF({ ...f, role })}
            >
              <SelectTrigger data-testid="invite-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ROLE_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Extra access for this person</Label>
            <ExtraAccess
              role={f.role}
              perms={perms}
              value={f.extra_perms}
              onChange={(extra_perms) => setF({ ...f, extra_perms })}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            data-testid="invite-cancel-button"
          >
            Cancel
          </Button>
          <Button
            disabled={busy || !f.email || !f.name}
            onClick={save}
            data-testid="invite-send-button"
          >
            {busy ? "Sending…" : "Send invitation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StaffSetupDialog({ onClose, onSaved, perms }) {
  const [f, setF] = useState({
    email: "",
    name: "",
    role: "teacher",
    title: "",
    extra_perms: [],
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/auth/invitations/drafts", f);
      toast.success("Staff setup saved. No invitation has been sent.");
      onSaved();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-h-[90vh] max-w-md overflow-y-auto"
        data-testid="staff-setup-dialog"
      >
        <DialogHeader>
          <DialogTitle>Prepare a staff member</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Email</Label>
            <Input
              type="email"
              value={f.email}
              onChange={(event) => setF({ ...f, email: event.target.value })}
              data-testid="staff-setup-email-input"
            />
          </div>
          <div className="space-y-1">
            <Label>Name</Label>
            <Input
              value={f.name}
              onChange={(event) => setF({ ...f, name: event.target.value })}
              data-testid="staff-setup-name-input"
            />
          </div>
          <div className="space-y-1">
            <Label>Job title</Label>
            <Input
              value={f.title}
              onChange={(event) => setF({ ...f, title: event.target.value })}
              data-testid="staff-setup-title-input"
            />
          </div>
          <div className="space-y-1">
            <Label>Role</Label>
            <Select
              value={f.role}
              onValueChange={(role) => setF({ ...f, role })}
            >
              <SelectTrigger data-testid="staff-setup-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="teacher">Teacher</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Extra access for this person</Label>
            <ExtraAccess
              role={f.role}
              perms={perms}
              value={f.extra_perms}
              onChange={(extra_perms) => setF({ ...f, extra_perms })}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            data-testid="staff-setup-cancel-button"
          >
            Cancel
          </Button>
          <Button
            disabled={busy || !f.email || !f.name}
            onClick={save}
            data-testid="staff-setup-save-button"
          >
            {busy ? "Saving…" : "Save setup"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const DEMO_LABELS = {
  demo_users: "DEMO staff accounts",
  practicals: "DEMO practicals",
  timetable: "DEMO timetable entries",
  classes: "DEMO classes",
  labs: "DEMO labs",
  inventory: "DEMO inventory",
};

function DemoDataDialog({ onClose, onRemoved }) {
  const [counts, setCounts] = useState(null);
  const [selected, setSelected] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api
      .get("/admin/demo-data/summary")
      .then((response) => {
        setCounts(response.data.counts);
        setSelected(
          Object.keys(response.data.counts).filter(
            (key) => response.data.counts[key] > 0,
          ),
        );
      })
      .catch((error) => toast.error(errMsg(error)));
  }, []);
  const toggle = (key) =>
    setSelected((current) =>
      current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key],
    );
  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/demo-data/remove", {
        selected,
        confirmation: "REMOVE DEMO DATA",
      });
      const remaining = Object.values(data.remaining).reduce(
        (total, count) => total + count,
        0,
      );
      toast.success(
        remaining
          ? `Selected demo data removed. ${remaining} demo records remain.`
          : "All selected demo data removed.",
      );
      onRemoved();
      onClose();
    } catch (error) {
      toast.error(errMsg(error));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };
  const timetableRequired =
    selected.some((key) => ["demo_users", "classes", "labs"].includes(key)) &&
    !selected.includes("timetable");
  return (
    <>
      <Dialog open={!confirming} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-lg" data-testid="demo-data-dialog">
          <DialogHeader>
            <DialogTitle>Remove demo data</DialogTitle>
          </DialogHeader>
          {!counts ? (
            <p
              className="text-sm text-muted-foreground"
              data-testid="demo-data-loading"
            >
              Checking demo data…
            </p>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Choose the walkthrough records to permanently remove.
              </p>
              <div
                className="divide-y rounded-lg border"
                data-testid="demo-data-options"
              >
                {Object.entries(DEMO_LABELS).map(([key, label]) => (
                  <label
                    key={key}
                    className="flex items-center gap-3 px-3 py-3"
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(key)}
                      disabled={!counts[key]}
                      onChange={() => toggle(key)}
                      data-testid={`demo-data-checkbox-${key}`}
                    />
                    <span className="flex-1 text-sm">{label}</span>
                    <span
                      className="text-xs text-muted-foreground"
                      data-testid={`demo-data-count-${key}`}
                    >
                      {counts[key]}
                    </span>
                  </label>
                ))}
              </div>
              {timetableRequired && (
                <p
                  className="text-sm text-destructive"
                  data-testid="demo-data-dependency-error"
                >
                  Select DEMO timetable entries before removing DEMO staff,
                  classes or labs.
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={onClose}
              data-testid="demo-data-cancel-button"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!selected.length || timetableRequired || !counts}
              onClick={() => setConfirming(true)}
              data-testid="demo-data-review-remove-button"
            >
              Remove selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent data-testid="demo-data-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove selected demo data?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. Your real school admin account and records
              are kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="demo-data-confirm-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={remove}
              disabled={busy}
              data-testid="demo-data-confirm-remove"
            >
              {busy ? "Removing…" : "Remove demo data"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function Users() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [edit, setEdit] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [discardingSetup, setDiscardingSetup] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [demoCleanup, setDemoCleanup] = useState(false);
  const [perms, setPerms] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/admin/permissions").then((r) => setPerms(r.data));
    api.get("/auth/invitations").then((r) => setInvitations(r.data));
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const resend = async (invite) => {
    try {
      await api.post(`/auth/invitations/${invite.id}/resend`);
      toast.success("A fresh invitation link has been sent");
      load();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };
  const revoke = async (invite) => {
    try {
      await api.delete(`/auth/invitations/${invite.id}`);
      toast.success("Invitation revoked");
      load();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };
  const sendSetupInvitation = async (setup) => {
    try {
      await api.post(`/auth/invitations/${setup.id}/send`);
      toast.success("Account invitation sent");
      load();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };
  const discardSetup = async () => {
    if (!discardingSetup) {
      return;
    }
    try {
      await api.delete(`/auth/invitations/${discardingSetup.id}`);
      toast.success("Staff setup discarded");
      setDiscardingSetup(null);
      load();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };
  const setups = invitations.filter((invite) => invite.status === "draft");
  const sentInvitations = invitations.filter((invite) => invite.status !== "draft");
  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <Button
          onClick={() => setInviting(true)}
          data-testid="invite-user-button"
        >
          <MailPlus className="h-4 w-4 mr-2" />
          Invite user
        </Button>
        <Button
          variant="outline"
          onClick={() => setPreparing(true)}
          data-testid="prepare-staff-button"
        >
          <Plus className="mr-2 h-4 w-4" />
          Prepare staff member
        </Button>
        <Button
          variant="outline"
          onClick={() => setDemoCleanup(true)}
          data-testid="demo-data-open-button"
        >
          Remove demo data
        </Button>
      </div>
      <div
        className="rounded-xl border bg-card divide-y"
        data-testid="staff-setups-list"
      >
        <div className="px-4 py-3 text-sm font-semibold">Staff being prepared</div>
        {setups.length === 0 ? (
          <div
            className="px-4 py-5 text-sm text-muted-foreground"
            data-testid="staff-setups-empty"
          >
            No staff setups saved yet.
          </div>
        ) : (
          setups.map((setup) => (
            <div
              key={setup.id}
              className="flex flex-wrap items-center gap-3 p-4"
              data-testid={`staff-setup-row-${setup.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium">{setup.name}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {setup.email} · {ROLE_LABELS[setup.role] || setup.role}
                </div>
                <div
                  className="mt-1 text-[11px] text-muted-foreground"
                  data-testid={`staff-setup-timetable-count-${setup.id}`}
                >
                  {setup.timetable_count || 0} timetable lesson
                  {setup.timetable_count === 1 ? "" : "s"} saved
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/timetable?staff=${setup.id}`)}
                data-testid={`staff-setup-build-timetable-${setup.id}`}
              >
                Build timetable
              </Button>
              <Button
                size="sm"
                disabled={!setup.timetable_count}
                onClick={() => sendSetupInvitation(setup)}
                data-testid={`staff-setup-send-invitation-${setup.id}`}
              >
                Send invitation
              </Button>
              <Button
                size="icon"
                variant="ghost"
                title="Discard staff setup"
                onClick={() => setDiscardingSetup(setup)}
                data-testid={`staff-setup-discard-${setup.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))
        )}
      </div>
      <div
        className="rounded-xl border bg-card divide-y"
        data-testid="users-list"
      >
        {users.map((u) => (
          <button
            key={u.id}
            onClick={() => setEdit(u)}
            className="w-full p-4 flex items-center gap-4 text-left hover:bg-muted/40"
            data-testid={`user-row-${u.id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{u.name}</div>
              <div className="text-xs text-muted-foreground truncate">
                {u.email}
              </div>
            </div>
            {u.extra_perms?.length > 0 && (
              <span
                className="text-[11px] rounded-full bg-primary/10 text-primary px-2 py-0.5"
                data-testid={`user-extra-badge-${u.id}`}
              >
                +{u.extra_perms.length} extra
              </span>
            )}
            <span className="text-xs">{ROLE_LABELS[u.role] || u.role}</span>
            {!u.active && (
              <span className="text-xs text-destructive">Inactive</span>
            )}
          </button>
        ))}
      </div>
      <div
        className="rounded-xl border bg-card divide-y"
        data-testid="invitations-list"
      >
        <div className="px-4 py-3 text-sm font-semibold">
          Account invitations
        </div>
        {sentInvitations.length === 0 ? (
          <div
            className="px-4 py-5 text-sm text-muted-foreground"
            data-testid="invitations-empty"
          >
            No invitations sent yet.
          </div>
        ) : (
          sentInvitations.map((invite) => (
            <div
              key={invite.id}
              className="flex flex-wrap items-center gap-3 p-4"
              data-testid={`invitation-row-${invite.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium">{invite.name}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {invite.email} · {ROLE_LABELS[invite.role] || invite.role}
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">
                  {invite.status === "pending"
                    ? `Expires ${new Date(invite.expires_at).toLocaleDateString("en-GB")}`
                    : invite.status}
                </div>
              </div>
              {invite.status !== "accepted" && (
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => resend(invite)}
                  title="Resend invitation"
                  data-testid={`invitation-resend-${invite.id}`}
                >
                  <RotateCw className="h-4 w-4" />
                </Button>
              )}
              {invite.status === "pending" && (
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => revoke(invite)}
                  title="Revoke invitation"
                  data-testid={`invitation-revoke-${invite.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))
        )}
      </div>
      {edit && (
        <UserDialog
          u={edit}
          perms={perms}
          onClose={() => setEdit(null)}
          onSaved={() => {
            setEdit(null);
            load();
          }}
        />
      )}
      {inviting && (
        <InviteDialog
          perms={perms}
          onClose={() => setInviting(false)}
          onSaved={() => {
            setInviting(false);
            load();
          }}
        />
      )}
      {preparing && (
        <StaffSetupDialog
          perms={perms}
          onClose={() => setPreparing(false)}
          onSaved={() => {
            setPreparing(false);
            load();
          }}
        />
      )}
      <AlertDialog
        open={Boolean(discardingSetup)}
        onOpenChange={(open) => !open && setDiscardingSetup(null)}
      >
        <AlertDialogContent data-testid="staff-setup-discard-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard this staff setup?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes {discardingSetup?.name || "this staff member"} and their
              saved timetable. No invitation will be sent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="staff-setup-discard-cancel-button">
              Keep setup
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={discardSetup}
              data-testid="staff-setup-discard-confirm-button"
            >
              Discard setup
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {demoCleanup && (
        <DemoDataDialog
          onClose={() => setDemoCleanup(false)}
          onRemoved={load}
        />
      )}
    </div>
  );
}

function Classes() {
  const [meta, setMeta] = useState(null);
  const [cls, setCls] = useState({ name: "", year_group: "", size: "" });
  const load = useCallback(
    () => api.get("/timetable/meta").then((r) => setMeta(r.data)),
    [],
  );
  useEffect(() => {
    load();
  }, [load]);
  const add = async (coll, body) => {
    try {
      await api.post(`/admin/${coll}`, body);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const saveSize = async (c, size) => {
    await api.put(`/admin/classes/${c.id}`, {
      name: c.name,
      year_group: c.year_group,
      size: Number(size),
    });
    toast.success(`${c.name} updated`);
  };
  if (!meta) return null;
  return (
    <div className="max-w-2xl rounded-xl border bg-card p-4 space-y-3" data-testid="classes-tab-panel">
      <h3 className="font-semibold">Classes</h3>
      <div className="max-h-72 overflow-y-auto divide-y">
        {meta.classes.map((c) => (
          <div key={c.id} className="flex items-center gap-3 py-1.5 text-sm">
            <span className="flex-1 font-medium">{c.name}</span>
            <span className="text-xs text-muted-foreground">
              Year {c.year_group}
            </span>
            <Input
              type="number"
              defaultValue={c.size}
              onBlur={(e) =>
                Number(e.target.value) !== c.size &&
                saveSize(c, e.target.value)
              }
              className="h-8 w-20"
              data-testid={`class-size-${c.name}`}
            />
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          placeholder="Name"
          value={cls.name}
          onChange={(e) => setCls({ ...cls, name: e.target.value })}
          data-testid="new-class-name"
        />
        <Input
          placeholder="Year"
          type="number"
          value={cls.year_group}
          onChange={(e) => setCls({ ...cls, year_group: e.target.value })}
          className="w-20"
          data-testid="new-class-year-input"
        />
        <Input
          placeholder="Pupils"
          type="number"
          value={cls.size}
          onChange={(e) => setCls({ ...cls, size: e.target.value })}
          className="w-20"
          data-testid="new-class-size-input"
        />
        <Button
          disabled={!cls.name}
          onClick={() => {
            add("classes", {
              name: cls.name,
              year_group: Number(cls.year_group) || null,
              size: Number(cls.size) || 30,
            });
            setCls({ name: "", year_group: "", size: "" });
          }}
          data-testid="add-class-button"
        >
          Add
        </Button>
      </div>
    </div>
  );
}

function Settings() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data));
  }, []);
  const save = async () => {
    try {
      const { data } = await api.put("/admin/settings", {
        ...s,
        default_group_size: Number(s.default_group_size),
        max_sets: Number(s.max_sets),
      });
      setS(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!s) return null;
  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Timezone</Label>
          <Input
            value={s.timezone}
            onChange={(e) => setS({ ...s, timezone: e.target.value })}
            data-testid="settings-timezone"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">
            Pupils per group (for suggested sets)
          </Label>
          <Input
            type="number"
            value={s.default_group_size}
            onChange={(e) => setS({ ...s, default_group_size: e.target.value })}
            data-testid="settings-group-size"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Maximum suggested sets</Label>
          <Input
            type="number"
            value={s.max_sets}
            onChange={(e) => setS({ ...s, max_sets: e.target.value })}
            data-testid="settings-max-sets"
          />
        </div>
      </div>
      <CalendarSettingsForms settings={s} onChange={setS} />
      <Button onClick={save} data-testid="settings-save-button">
        Save settings
      </Button>
      <div className="rounded-xl border p-4 text-sm">
        <span className="font-semibold">Subscription: </span>
        {s.subscription?.plan} · {s.subscription?.seats} seats · school code{" "}
        {s.code}
      </div>
    </div>
  );
}

function Branding() {
  const { user, checkAuth } = useAuth();
  const up = async (kind, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/branding/${kind}`, fd);
      await checkAuth();
      toast.success("Updated");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const reset = async (kind) => {
    await api.delete(`/admin/branding/${kind}`);
    await checkAuth();
  };
  const b = user.school?.branding || {};
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {[
        ["logo", "Ask Jonny logo"],
        ["avatar", "Jonny chat avatar"],
      ].map(([k, l]) => (
        <div key={k} className="rounded-xl border bg-card p-5 space-y-3">
          <h3 className="font-semibold">{l}</h3>
          <div className="h-24 w-24 rounded-xl border bg-muted grid place-items-center overflow-hidden">
            {b[k] ? (
              <img
                src={b[k]}
                alt={l}
                className="h-full w-full object-contain"
                data-testid={`branding-${k}-preview`}
              />
            ) : (
              <ImagePlus className="h-6 w-6 text-muted-foreground" />
            )}
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline" size="sm">
              <label className="cursor-pointer">
                Upload
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) =>
                    e.target.files[0] && up(k, e.target.files[0])
                  }
                  data-testid={
                    k === "avatar" ? "avatar-upload-input" : "logo-upload-input"
                  }
                />
              </label>
            </Button>
            {b[k] && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => reset(k)}
                data-testid={`branding-${k}-reset`}
              >
                Reset
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            PNG, JPG or SVG under 1MB.
          </p>
        </div>
      ))}
    </div>
  );
}

function Audit() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/admin/audit").then((r) => setRows(r.data));
  }, []);
  return (
    <div
      className="rounded-xl border bg-card divide-y text-sm"
      data-testid="audit-log"
    >
      {rows.map((r) => (
        <div key={r.id} className="p-3 flex gap-3">
          <span className="text-xs text-muted-foreground w-36 shrink-0">
            {new Date(r.created_at).toLocaleString("en-GB")}
          </span>
          <span className="flex-1">
            {r.user_name} —{" "}
            <span className="font-mono text-xs">{r.action}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

const ROLE_COLS = ["teacher", "technician", "admin"];

function Permissions() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const apply = (d) => {
    setData(d);
    setDraft({
      teacher: [...d.roles.teacher],
      technician: [...d.roles.technician],
    });
  };
  useEffect(() => {
    api.get("/admin/permissions").then((r) => apply(r.data));
  }, []);
  if (!data) return null;
  const groups = [...new Set(data.catalog.map((c) => c.group))];
  const toggle = (role, key) =>
    setDraft((d) => ({
      ...d,
      [role]: d[role].includes(key)
        ? d[role].filter((k) => k !== key)
        : [...d[role], key],
    }));
  const dirty = data.editable_roles.filter(
    (r) => [...draft[r]].sort().join() !== [...data.roles[r]].sort().join(),
  );
  const save = async () => {
    setSaving(true);
    try {
      let last = null;
      for (const role of dirty)
        last = (
          await api.put("/admin/permissions", { role, perms: draft[role] })
        ).data;
      if (last) apply(last);
      toast.success(
        "Permissions saved. Changes apply on each user's next page load.",
      );
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  const reset = async (role) => {
    const r = await api.delete(`/admin/permissions/${role}`);
    apply(r.data);
    toast.success(`${ROLE_LABELS[role]} permissions reset to defaults`);
  };
  return (
    <div className="space-y-4" data-testid="permissions-panel">
      <p className="text-sm text-muted-foreground max-w-2xl">
        Choose what each level can see and do. Pages without permission are
        hidden from the menu, blocked on the server and Jonny won't use them.
        Admins always have full access. To give one person extra access, open
        them in the Users tab.
      </p>
      <div className="rounded-xl border bg-card overflow-x-auto">
        <table
          className="w-full text-sm min-w-[680px]"
          data-testid="permissions-table"
        >
          <thead>
            <tr className="border-b text-left">
              <th className="p-3 font-medium">Permission</th>
              {ROLE_COLS.map((r) => (
                <th key={r} className="p-3 font-medium text-center w-36">
                  <div>{ROLE_LABELS[r]}</div>
                  {data.editable_roles.includes(r) && (
                    <button
                      className="text-[11px] text-primary hover:underline font-normal"
                      onClick={() => reset(r)}
                      data-testid={`permissions-reset-${r}`}
                    >
                      Reset to default
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => [
              <tr key={g} className="bg-muted/50">
                <td
                  colSpan={5}
                  className="px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold"
                >
                  {g}
                </td>
              </tr>,
              ...data.catalog
                .filter((c) => c.group === g)
                .map((c) => (
                  <tr key={c.key} className="border-t">
                    <td className="p-3">{c.label}</td>
                    {ROLE_COLS.map((r) => (
                      <td key={r} className="p-3 text-center">
                        {data.editable_roles.includes(r) ? (
                          <Switch
                            checked={draft[r].includes(c.key)}
                            onCheckedChange={() => toggle(r, c.key)}
                            data-testid={`perm-${r}-${c.key.replace(":", "-")}`}
                          />
                        ) : (
                          <span
                            className="text-xs text-muted-foreground"
                            data-testid={`perm-${r}-${c.key.replace(":", "-")}`}
                          >
                            Always
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                )),
            ])}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-3">
        <Button
          onClick={save}
          disabled={!dirty.length || saving}
          data-testid="permissions-save-button"
        >
          {saving ? "Saving…" : "Save permissions"}
        </Button>
        {dirty.length > 0 && (
          <span
            className="text-xs text-muted-foreground"
            data-testid="permissions-unsaved"
          >
            Unsaved changes for {dirty.map((r) => ROLE_LABELS[r]).join(" and ")}
          </span>
        )}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { can } = useAuth();
  return (
    <Page>
      <PageHeader
        title="Administration"
        subtitle="Users, labs, storage, classes, school calendar and branding"
      />
      <Tabs defaultValue="users">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="users" data-testid="admin-tab-users">
            Users
          </TabsTrigger>
          <TabsTrigger value="labs-storage" data-testid="admin-tab-labs-storage">
            Labs & storage
          </TabsTrigger>
          <TabsTrigger value="classes" data-testid="admin-tab-classes">
            Classes
          </TabsTrigger>
          <TabsTrigger value="permissions" data-testid="admin-tab-permissions">
            Permissions
          </TabsTrigger>
          <TabsTrigger value="ask-jonny" data-testid="admin-tab-ask-jonny">
            Ask Jonny
          </TabsTrigger>
          {can("settings:write") && (
            <TabsTrigger value="settings" data-testid="admin-tab-settings">
              Calendar & settings
            </TabsTrigger>
          )}
          {can("settings:write") && (
            <TabsTrigger value="branding" data-testid="admin-tab-branding">
              Branding
            </TabsTrigger>
          )}
          <TabsTrigger value="audit" data-testid="admin-tab-audit">
            Audit log
          </TabsTrigger>
        </TabsList>
        <TabsContent value="users">
          <Users />
        </TabsContent>
        <TabsContent value="labs-storage" data-testid="labs-storage-tab-panel">
          <RoomsManager />
        </TabsContent>
        <TabsContent value="classes">
          <Classes />
        </TabsContent>
        <TabsContent value="permissions">
          <Permissions />
        </TabsContent>
        <TabsContent value="ask-jonny">
          <StarterQuestionsSettings />
        </TabsContent>
        <TabsContent value="settings">
          <Settings />
        </TabsContent>
        <TabsContent value="branding">
          <Branding />
        </TabsContent>
        <TabsContent value="audit">
          <Audit />
        </TabsContent>
      </Tabs>
    </Page>
  );
}
